import { Link } from "@tanstack/react-router";
import type { SafetyVideoStatus } from "@unbox/api";
import { Announcement03, ArrowUpRight, MinusCircle, TrendUp01, VideoRecorder } from "@untitledui/icons";
import { Camera, MoreVertical, Play, VideoOff } from "lucide-react";
import { useState } from "react";
import { useVideoCollaborations } from "@/api/collaborations";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { Badge } from "@/components/base/badges/badges";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ManageCampaignsModal } from "@/pages/creators/tabs/manage-campaigns-modal";
import { useMerchant } from "@/providers/MerchantProvider";
import { videoExplorerRoute } from "@/router";
import { formatCreatorId } from "@/utils/formatters";

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatVideoDate = (date: Date) => {
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day}${getOrdinalSuffix(day)} ${month} ${year} @${time}`;
};

type VideosTabProps = {
  related:
    | {
        creators: Array<{
          creatorId: string;
          creatorName: string;
          thumbnail: string | null;
          gmv: number;
          videos: number;
          [key: string]: any;
        }>;
        videos: Array<{
          videoId: string;
          creatorId: string | null;
          creatorThumbnail?: string | null;
          urlFirstFrame: string | null;
          videoUrl: string | null;
          videoTitle: string;
          publishDate: string;
          gmv: number;
          unitSales: number;
          views: number;
          gpm: number;
          likes: number | null;
          flagCount: number;
          flagTypes: string[];
          safetyStatus: SafetyVideoStatus;
          isInAdCampaign?: boolean;
          campaignCount?: number;
          [key: string]: any;
        }>;
      }
    | null
    | undefined;
  currencyFormatter: Intl.NumberFormat;
  numFormatter: Intl.NumberFormat;
  productId: string;
  productName: string;
};

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

export const VideosTab = ({ related, currencyFormatter, productId, productName }: VideosTabProps) => {
  const [manageCampaignVideoId, setManageCampaignVideoId] = useState<string | null>(null);
  const [removeCollabVideoId, setRemoveCollabVideoId] = useState<string | null>(null);
  const merchant = useMerchant();

  if (!related) {
    return (
      <div className="flex w-full h-[400px] justify-center items-center mt-8">
        <LoadingIndicator />
      </div>
    );
  }

  if (related.videos.length === 0) {
    return (
      <div className="w-full h-[400px] bg-gray-50 rounded-lg flex flex-col items-center justify-center text-center mt-8">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: "#F5F5F5" }}
        >
          <VideoOff className="w-6 h-6" style={{ color: "#717680" }} />
        </div>
        <h4 className="text-sm font-medium text-primary mb-1">No videos selling this product yet</h4>
        <p className="text-sm text-tertiary max-w-md">
          Top-performing videos will auto appear here, as <br /> creators post.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-8">
      <div className="flex items-center gap-2">
        <TrendUp01 className="w-5 h-5 text-tertiary" />
        <h3 className="text-sm font-medium text-secondary">Videos driving top GMV in the last 30 days</h3>
      </div>

      <div className="space-y-4">
        {related.videos.map((video) => {
          const videoDate = new Date(video.publishDate);

          return (
            <div
              key={video.videoId}
              className="border border-border-primary rounded-lg hover:border-gray-300 transition-colors relative"
            >
              <div className="flex rounded-lg overflow-hidden">
                {/* Video Thumbnail with Play Button */}
                <div className="relative w-48 flex-shrink-0 bg-black self-stretch overflow-hidden">
                  {video.urlFirstFrame ? (
                    <>
                      <img
                        src={video.urlFirstFrame}
                        alt={video.videoTitle}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {video.isInAdCampaign && (
                        <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded px-2 py-0.5 text-xs font-medium text-gray-700 z-10 flex items-center gap-1 rounded-full">
                          <Announcement03 className="w-3.5 h-3.5 text-gray-700" />
                          <span className="text-xs font-medium text-gray-700">Ad</span>
                        </div>
                      )}
                      <Link
                        to={videoExplorerRoute.to}
                        params={{ merchantId: merchant }}
                        search={{ video_id: video.videoId }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded p-1.5 hover:bg-white transition-colors z-10"
                      >
                        <Play className="w-4 h-4 text-gray-700" />
                      </Link>
                    </>
                  ) : (
                    <div className="absolute inset-0 w-full h-full bg-gray-100 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="flex-1 flex flex-col min-w-0 relative">
                  {/* Risk Badge and Menu - Top Right Corner */}
                  <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                    <DetailedVideoStatusBadge status={video.safetyStatus} showInfoIcon={true} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0 transition-colors"
                          aria-label="Video actions"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={5}>
                        <DropdownMenuItem asChild>
                          <Link
                            to={videoExplorerRoute.to}
                            params={{ merchantId: merchant }}
                            search={{ video_id: video.videoId }}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cursor-pointer flex gap-2"
                          >
                            <VideoRecorder className="w-4 h-4 text-tertiary" />
                            See Video in Detail
                          </Link>
                        </DropdownMenuItem>
                        {video.isInAdCampaign && (
                          <DropdownMenuItem
                            onSelect={() => {
                              setManageCampaignVideoId(video.videoId);
                            }}
                          >
                            <MinusCircle className="w-4 h-4 text-tertiary" />
                            Remove From Ads campaign
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => setRemoveCollabVideoId(video.videoId)}
                          disabled={!video.creatorId}
                          className={!video.creatorId ? "text-gray-400" : "flex gap-2"}
                        >
                          <Announcement03 className="w-4 h-4 text-tertiary" />
                          Remove creator from collab
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Section 1: Info */}
                  <div className="p-4 pr-20">
                    {/* Creator Avatar and ID */}
                    <div className="flex items-center gap-2 mb-1.5">
                      {video.creatorThumbnail ? (
                        <img
                          src={video.creatorThumbnail}
                          alt={video.creatorId || "Creator"}
                          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs text-gray-500">?</span>
                        </div>
                      )}
                      {video.creatorId ? (
                        <a
                          href={`/creator-explorer?creator_id=${video.creatorId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-primary hover:text-brand-primary transition-colors"
                        >
                          {formatCreatorId(video.creatorId)}
                        </a>
                      ) : (
                        <span className="text-sm text-tertiary">Unknown creator</span>
                      )}
                    </div>

                    {/* Date */}
                    <p className="text-xs text-tertiary mb-2">{formatVideoDate(videoDate)}</p>

                    {/* Video Title - Truncated */}
                    <p className="text-sm text-secondary mb-3 line-clamp-2">{video.videoTitle}</p>

                    {/* Risks Identified */}
                    {video.flagCount > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs text-tertiary font-medium">Risks Identified</p>
                        <div className="flex flex-wrap gap-1.5">
                          {video.flagTypes.map((risk, idx) => (
                            <Badge key={`${video.videoId}-${risk}-${idx}`} color="gray" size="sm">
                              {risk}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section 2: Stats */}
                  <div>
                    <div className="border-t border-border-primary" />
                    <div className="grid grid-cols-4 gap-4 p-4">
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">GMV</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {currencyFormatter.format(video.gmv)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">Views</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {numFormatter.format(video.views)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">Likes</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {video.likes != null ? numFormatter.format(video.likes) : "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">Sales</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {numFormatter.format(video.unitSales)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* See All Videos Link */}
      {related && related.videos.length > 0 && (
        <Link
          to={videoExplorerRoute.to}
          params={{ merchantId: merchant }}
          search={{ product: [{ id: productId, label: productName }] }}
          className="flex items-center gap-1 text-sm text-secondary hover:text-primary font-medium transition-colors"
        >
          See all videos for this product
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      )}

      {/* Remove Creator from Collab Modal */}
      <RemoveCreatorFromCollabModalWrapper
        videoId={removeCollabVideoId}
        videos={related.videos}
        onClose={() => setRemoveCollabVideoId(null)}
      />

      {/* Manage Campaigns Modal */}
      {manageCampaignVideoId && (
        <ManageCampaignsModal
          videoId={manageCampaignVideoId}
          open={!!manageCampaignVideoId}
          onOpenChange={(open) => {
            if (!open) setManageCampaignVideoId(null);
          }}
        />
      )}
    </div>
  );
};

type RemoveCreatorFromCollabModalWrapperProps = {
  videoId: string | null;
  videos: Array<{
    videoId: string;
    creatorId: string | null;
    creatorThumbnail?: string | null;
    [key: string]: any;
  }>;
  onClose: () => void;
};

const RemoveCreatorFromCollabModalWrapper = ({
  videoId,
  videos,
  onClose,
}: RemoveCreatorFromCollabModalWrapperProps) => {
  const { data: collaborationsData } = useVideoCollaborations(
    videoId ? { mode: "include", videoIds: [videoId] } : null,
  );

  if (!videoId) return null;

  const video = videos.find((v) => v.videoId === videoId);
  if (!video || !video.creatorId) return null;

  const targetCollaborations =
    collaborationsData?.targetCollaborations.map((collab) => ({
      collaborationId: collab.collaborationId,
      name: collab.name,
      status: "Active", // Target collaborations are always active
    })) || [];

  return (
    <RemoveCreatorFromCollabModal
      creator={{
        creatorId: video.creatorId,
        creatorName: formatCreatorId(video.creatorId),
        thumbnail: video.creatorThumbnail,
        targetCollaborations,
      }}
      open={!!videoId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSuccess={onClose}
    />
  );
};
